import React, { useRef, useState } from 'react';
import {
  Box,
  Divider,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  Paper,
  Button,
  FormControlLabel,
  Checkbox,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

// ============================================
// FORMULÁRIO: CRIAR CONTRATO
// ============================================

interface CriarContratoFormProps extends FormProps {}

export const CriarContratoForm: React.FC<CriarContratoFormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [novaClausula, setNovaClausula] = useState('');

  const clausulasDisponiveis = [
    { id: 'preco_pagamento', label: 'Preço e Condições de Pagamento', descricao: 'Valor, forma de pagamento, datas, juros e multas' },
    { id: 'prazos_entrega', label: 'Prazos e Condições de Entrega/Execução', descricao: 'Prazos, prorrogações e consequências de atraso' },
    { id: 'obrigacoes', label: 'Obrigações das Partes', descricao: 'Obrigações específicas de cada parte' },
    { id: 'garantias', label: 'Garantias', descricao: 'Garantias oferecidas e prazo de garantia' },
    { id: 'rescisao', label: 'Rescisão Contratual', descricao: 'Hipóteses e consequências da rescisão' },
    { id: 'confidencialidade', label: 'Confidencialidade', descricao: 'Sigilo de informações trocadas' },
    { id: 'propriedade_intelectual', label: 'Propriedade Intelectual', descricao: 'Titularidade de patentes, marcas e direitos autorais' },
    { id: 'foro', label: 'Foro', descricao: 'Foro competente para litígios' },
    { id: 'disposicoes_gerais', label: 'Disposições Gerais', descricao: 'Cessão, alteração, notificação, tolerância' },
    { id: 'lei_aplicavel', label: 'Lei Aplicável', descricao: 'Lei brasileira para interpretação' },
    { id: 'clausula_penal', label: 'Cláusula Penal', descricao: 'Multa por descumprimento' },
    { id: 'caso_fortuito', label: 'Caso Fortuito ou Força Maior', descricao: 'Eventos imprevisíveis e inevitáveis' },
  ];

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleClausulaToggle = (clausulaId: string) => {
    const clausulasSelecionadas = values.clausulas_selecionadas || [];
    const novasClausulas = clausulasSelecionadas.includes(clausulaId)
      ? clausulasSelecionadas.filter((c: string) => c !== clausulaId)
      : [...clausulasSelecionadas, clausulaId];
    handleFieldChange('clausulas_selecionadas', novasClausulas);
  };

  const handleAdicionarClausulaPersonalizada = () => {
    if (novaClausula.trim()) {
      const clausulasPersonalizadas = values.clausulas_personalizadas || [];
      handleFieldChange('clausulas_personalizadas', [...clausulasPersonalizadas, novaClausula.trim()]);
      setNovaClausula('');
    }
  };

  const handleRemoverClausulaPersonalizada = (index: number) => {
    const clausulasPersonalizadas = values.clausulas_personalizadas || [];
    handleFieldChange('clausulas_personalizadas', clausulasPersonalizadas.filter((_: any, i: number) => i !== index));
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Criar Contrato"
        customDescription="Elaboração de contrato personalizado com cláusulas específicas"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Tipo de Contrato */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Qual é o tipo de Contrato? <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Contrato de Aluguel, Contrato de Prestação de Serviços, etc."
            value={values.tipo_contrato || ''}
            onChange={(e) => handleFieldChange('tipo_contrato', e.target.value)}
          />
        </Box>

        {/* Partes do Contrato */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Partes do Contrato <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Informe as partes envolvidas no contrato (contratante, contratado, locador, locatário, etc.)"
            value={values.partes_contrato || ''}
            onChange={(e) => handleFieldChange('partes_contrato', e.target.value)}
          />
        </Box>

        {/* Objeto do Contrato */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Detalhe o Objeto do Contrato <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva detalhadamente o objeto do contrato, incluindo: finalidade, valores, prazos, responsabilidades, etc."
            value={values.objeto_contrato || ''}
            onChange={(e) => handleFieldChange('objeto_contrato', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {(values.objeto_contrato || '').length} de 10000 caracteres
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Cláusulas */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Cláusulas do Contrato
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione as cláusulas que deseja incluir no contrato:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {clausulasDisponiveis.map((clausula) => (
              <Paper
                key={clausula.id}
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: (values.clausulas_selecionadas || []).includes(clausula.id)
                    ? 'primary.main'
                    : 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  bgcolor: (values.clausulas_selecionadas || []).includes(clausula.id)
                    ? alpha(theme.palette.primary.main, 0.05)
                    : 'background.paper',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
                onClick={() => handleClausulaToggle(clausula.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Checkbox
                    checked={(values.clausulas_selecionadas || []).includes(clausula.id)}
                    sx={{ p: 0, mt: 0.5 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {clausula.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {clausula.descricao}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* Cláusulas Personalizadas */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Adicionar Cláusula Personalizada
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Descreva a cláusula personalizada que deseja adicionar"
              value={novaClausula}
              onChange={(e) => setNovaClausula(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAdicionarClausulaPersonalizada()}
            />
            <Button
              variant="outlined"
              onClick={handleAdicionarClausulaPersonalizada}
              startIcon={<AddIcon />}
            >
              Adicionar
            </Button>
          </Box>
          
          {(values.clausulas_personalizadas || []).length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(values.clausulas_personalizadas || []).map((clausula: string, index: number) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="body2">{clausula}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoverClausulaPersonalizada(index)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        {/* Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Documentos de referência, contratos anteriores ou modelos."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo detalhes adicionais do contrato."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="Quanto mais detalhes você fornecer sobre o objeto e as partes do contrato, mais preciso será o documento gerado." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: REVISAR CONTRATO
// ============================================

interface RevisarContratoFormProps extends FormProps {}

export const RevisarContratoForm: React.FC<RevisarContratoFormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const focosRevisao = [
    { id: 'riscos', label: 'Riscos Contratuais', descricao: 'Análise de cláusulas que podem gerar prejuízos' },
    { id: 'obrigacoes', label: 'Obrigações Excessivas', descricao: 'Identificar obrigações desproporcionais' },
    { id: 'prazos', label: 'Prazos e Multas', descricao: 'Verificar prazos e penalidades' },
    { id: 'garantias', label: 'Garantias', descricao: 'Avaliar garantias oferecidas e recebidas' },
    { id: 'rescisao', label: 'Rescisão', descricao: 'Condições de encerramento do contrato' },
    { id: 'confidencialidade', label: 'Confidencialidade', descricao: 'Cláusulas de sigilo e proteção de dados' },
    { id: 'propriedade_intelectual', label: 'Propriedade Intelectual', descricao: 'Direitos sobre criações e marcas' },
    { id: 'jurisdicao', label: 'Jurisdição e Foro', descricao: 'Local de resolução de conflitos' },
  ];

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleFocoToggle = (focoId: string) => {
    const focosSelecionados = values.focos_revisao || [];
    const novosFocos = focosSelecionados.includes(focoId)
      ? focosSelecionados.filter((f: string) => f !== focoId)
      : [...focosSelecionados, focoId];
    handleFieldChange('focos_revisao', novosFocos);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Revisar Contrato"
        customDescription="Análise e revisão de contrato existente"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload do Contrato */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Anexe o contrato para revisão <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <DocumentUploadArea
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            suggestedDocs="Contrato em formato .DOCX ou .PDF"
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        </Box>

        {/* Parte Representada */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Como é tratada a parte que você representa no contrato? <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: comprador, contratante, contratada, locatário, etc."
            value={values.parte_representada || ''}
            onChange={(e) => handleFieldChange('parte_representada', e.target.value)}
            helperText="Separe os nomes com ';' caso você represente mais de uma parte"
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Tipo de Revisão */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Selecione a forma de revisão
          </Typography>

          <ToggleButtonGroup
            value={values.tipo_revisao || 'automatica'}
            exclusive
            onChange={(_, newValue) => {
              if (newValue) handleFieldChange('tipo_revisao', newValue);
            }}
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              '& .MuiToggleButton-root': {
                flex: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px !important',
                py: 3,
                px: 3,
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  },
                },
              },
            }}
          >
            <ToggleButton value="automatica">
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  🚀 Revisão Automática (Modo Rápido)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  A IA analisa o contrato e sugere melhorias nos principais pontos críticos.
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="personalizada">
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  🎯 Revisão com Foco Personalizado
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Escolha os aspectos que deseja priorizar, e a IA concentrará a revisão neles.
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Focos de Revisão (apenas se personalizada) */}
        {values.tipo_revisao === 'personalizada' && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Selecione os focos da revisão:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {focosRevisao.map((foco) => (
                <Chip
                  key={foco.id}
                  label={foco.label}
                  onClick={() => handleFocoToggle(foco.id)}
                  color={(values.focos_revisao || []).includes(foco.id) ? 'primary' : 'default'}
                  variant={(values.focos_revisao || []).includes(foco.id) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Pontos de Atenção */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Pontos de atenção específicos (Opcional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Há algum ponto específico que deseja analisar? Ex: verificar se há cláusula de exclusividade, analisar prazo de confidencialidade, etc."
            value={values.pontos_atencao || ''}
            onChange={(e) => handleFieldChange('pontos_atencao', e.target.value)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="Anexe o contrato em formato legível (.DOCX ou .PDF) para uma análise mais precisa." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: ANÁLISE DE CONTRATO COM PARECER
// ============================================

export const AnaliseContratoParecerrForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Análise de Contrato com Parecer"
        customDescription="Parecer jurídico detalhado sobre contrato"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload do Contrato */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Contrato a ser analisado em formato .DOCX ou .PDF"
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Parte Representada */}
        <ClientNameField
          value={values.parte_representada || ''}
          onChange={(value) => handleFieldChange('parte_representada', value)}
          label="Qual parte você representa no contrato?"
        />

        {/* Objetivo da Análise */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Objetivo da análise <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Qual o objetivo desta análise? Ex: verificar viabilidade de assinatura, identificar riscos, analisar cláusulas específicas..."
            value={values.objetivo_analise || ''}
            onChange={(e) => handleFieldChange('objetivo_analise', e.target.value)}
          />
        </Box>

        {/* Contexto */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Contexto adicional (Opcional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Forneça informações adicionais relevantes para a análise..."
            value={values.contexto_adicional || ''}
            onChange={(e) => handleFieldChange('contexto_adicional', e.target.value)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O parecer jurídico incluirá análise de riscos, recomendações e pontos de atenção." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: ANÁLISE DE NEGOCIAÇÃO CONTRATUAL
// ============================================

export const AnaliseNegociacaoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Análise de Negociação Contratual"
        customDescription="Análise de propostas e contrapropostas contratuais"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload dos Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Contrato original, propostas e contrapropostas"
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Parte Representada */}
        <ClientNameField
          value={values.parte_representada || ''}
          onChange={(value) => handleFieldChange('parte_representada', value)}
          label="Qual parte você representa na negociação?"
        />

        {/* Objetivo da Negociação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Objetivo da negociação <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Quais são os principais objetivos do seu cliente na negociação? O que ele pretende alcançar?"
            value={values.objetivo_negociacao || ''}
            onChange={(e) => handleFieldChange('objetivo_negociacao', e.target.value)}
          />
        </Box>

        {/* Pontos Inegociáveis */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Pontos inegociáveis
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Quais cláusulas ou condições não podem ser alteradas?"
            value={values.pontos_inegociaveis || ''}
            onChange={(e) => handleFieldChange('pontos_inegociaveis', e.target.value)}
          />
        </Box>

        {/* Pontos Flexíveis */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Pontos flexíveis
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Quais cláusulas ou condições podem ser negociadas?"
            value={values.pontos_flexiveis || ''}
            onChange={(e) => handleFieldChange('pontos_flexiveis', e.target.value)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A análise incluirá sugestões de argumentos e estratégias de negociação." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: ANÁLISE DE RISCO DE RESCISÃO
// ============================================

export const AnaliseRiscoRescisaoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Análise de Risco de Rescisão"
        customDescription="Avaliação de riscos e consequências da rescisão contratual"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload do Contrato */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Contrato vigente a ser analisado"
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* Parte Representada */}
        <ClientNameField
          value={values.parte_representada || ''}
          onChange={(value) => handleFieldChange('parte_representada', value)}
          label="Qual parte você representa?"
        />

        {/* Motivo da Rescisão */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Motivo pretendido para a rescisão <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva o motivo pelo qual deseja rescindir o contrato..."
            value={values.motivo_rescisao || ''}
            onChange={(e) => handleFieldChange('motivo_rescisao', e.target.value)}
          />
        </Box>

        {/* Situação Atual */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Situação atual do contrato
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva a situação atual: há inadimplência? Descumprimento de obrigações? Problemas na execução?"
            value={values.situacao_atual || ''}
            onChange={(e) => handleFieldChange('situacao_atual', e.target.value)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A análise incluirá os riscos jurídicos, multas aplicáveis e estratégias para minimizar prejuízos." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: COMPARAR CONTRATOS
// ============================================

export const CompararContratosForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Comparar Contratos"
        customDescription="Comparação detalhada entre dois ou mais contratos"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload dos Contratos */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Anexe os contratos para comparação <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Anexe pelo menos 2 contratos para realizar a comparação
          </Typography>
          <DocumentUploadArea
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            suggestedDocs="Contratos a serem comparados (mínimo 2)"
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        </Box>

        {/* Foco da Comparação */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Foco da comparação
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Quais aspectos deseja comparar? Ex: valores, prazos, garantias, cláusulas específicas..."
            value={values.foco_comparacao || ''}
            onChange={(e) => handleFieldChange('foco_comparacao', e.target.value)}
          />
        </Box>

        {/* Objetivo */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Objetivo da comparação
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Para que você precisa dessa comparação? Ex: escolher a melhor proposta, identificar diferenças, verificar evolução..."
            value={values.objetivo_comparacao || ''}
            onChange={(e) => handleFieldChange('objetivo_comparacao', e.target.value)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A comparação destacará diferenças, semelhanças e pontos de atenção entre os contratos." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: CRIAR ADITIVO
// ============================================

export const CriarAditivoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Criar Aditivo Contratual"
        customDescription="Elaboração de termo aditivo ao contrato original"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload do Contrato Original */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Anexe o contrato original <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <DocumentUploadArea
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            suggestedDocs="Contrato original que será aditado"
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        </Box>

        {/* Número do Aditivo */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Número do Aditivo
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: 1º Termo Aditivo, 2º Termo Aditivo..."
            value={values.numero_aditivo || ''}
            onChange={(e) => handleFieldChange('numero_aditivo', e.target.value)}
          />
        </Box>

        {/* Alterações */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Alterações a serem feitas <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva detalhadamente as alterações a serem feitas no contrato original: mudança de valores, prorrogação de prazo, alteração de cláusulas, etc."
            value={values.alteracoes_aditivo || ''}
            onChange={(e) => handleFieldChange('alteracoes_aditivo', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Justificativa */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Justificativa das alterações
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Justifique o motivo das alterações propostas..."
            value={values.justificativa_aditivo || ''}
            onChange={(e) => handleFieldChange('justificativa_aditivo', e.target.value)}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O aditivo manterá todas as demais cláusulas do contrato original não expressamente alteradas." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: CRIAR CLÁUSULA
// ============================================

export const CriarClausulaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Criar Cláusula Contratual"
        customDescription="Elaboração de cláusula específica para contrato"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Tipo de Cláusula */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Tipo de cláusula <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Cláusula de Confidencialidade, Cláusula Penal, Cláusula de Rescisão..."
            value={values.tipo_clausula || ''}
            onChange={(e) => handleFieldChange('tipo_clausula', e.target.value)}
          />
        </Box>

        {/* Contexto do Contrato */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Contexto do contrato
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva o tipo de contrato onde a cláusula será inserida..."
            value={values.contexto_contrato || ''}
            onChange={(e) => handleFieldChange('contexto_contrato', e.target.value)}
          />
        </Box>

        {/* Objetivo da Cláusula */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Objetivo da cláusula <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="O que a cláusula deve prever? Quais situações ela deve cobrir?"
            value={values.objetivo_clausula || ''}
            onChange={(e) => handleFieldChange('objetivo_clausula', e.target.value)}
          />
        </Box>

        {/* Detalhes Específicos */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Detalhes específicos
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Valores, prazos, penalidades, exceções ou qualquer detalhe específico que a cláusula deve conter..."
            value={values.detalhes_clausula || ''}
            onChange={(e) => handleFieldChange('detalhes_clausula', e.target.value)}
          />
        </Box>

        {/* Upload de Referência */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Contrato de referência ou modelo de cláusula (opcional)"
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A cláusula será redigida de acordo com as melhores práticas jurídicas e legislação vigente." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: TERMO DE ENCERRAMENTO
// ============================================

export const TermoEncerramentoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Termo de Encerramento de Contrato"
        customDescription="Documento formal de encerramento contratual"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload do Contrato */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Anexe o contrato a ser encerrado <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <DocumentUploadArea
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            suggestedDocs="Contrato original que será encerrado"
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        </Box>

        {/* Motivo do Encerramento */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Motivo do encerramento <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva o motivo do encerramento do contrato: término do prazo, cumprimento do objeto, acordo entre as partes, etc."
            value={values.motivo_encerramento || ''}
            onChange={(e) => handleFieldChange('motivo_encerramento', e.target.value)}
          />
        </Box>

        {/* Data de Encerramento */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Data de encerramento
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={values.data_encerramento || ''}
            onChange={(e) => handleFieldChange('data_encerramento', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Pendências */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Pendências a serem resolvidas
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Há pagamentos pendentes, obrigações a cumprir ou outros pontos a serem resolvidos?"
            value={values.pendencias || ''}
            onChange={(e) => handleFieldChange('pendencias', e.target.value)}
          />
        </Box>

        {/* Quitação */}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.quitacao_mutua || false}
                onChange={(e) => handleFieldChange('quitacao_mutua', e.target.checked)}
              />
            }
            label="As partes dão quitação recíproca de todas as obrigações do contrato"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="O termo de encerramento formalizará o fim da relação contratual entre as partes." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: TERMOS DE USO
// ============================================

export const TermosUsoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Termos de Uso"
        customDescription="Elaboração de Termos de Uso para plataforma ou serviço"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Nome da Empresa/Plataforma */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Nome da empresa/plataforma <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Minha Empresa Ltda., Plataforma XYZ..."
            value={values.nome_empresa || ''}
            onChange={(e) => handleFieldChange('nome_empresa', e.target.value)}
          />
        </Box>

        {/* Tipo de Serviço */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Tipo de serviço/produto <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva o tipo de serviço ou produto oferecido: aplicativo, e-commerce, SaaS, marketplace, etc."
            value={values.tipo_servico || ''}
            onChange={(e) => handleFieldChange('tipo_servico', e.target.value)}
          />
        </Box>

        {/* Funcionalidades */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Principais funcionalidades
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Liste as principais funcionalidades do seu serviço/produto..."
            value={values.funcionalidades || ''}
            onChange={(e) => handleFieldChange('funcionalidades', e.target.value)}
          />
        </Box>

        {/* Dados Coletados */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados coletados dos usuários
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Quais dados são coletados? Ex: nome, e-mail, CPF, dados de pagamento, localização, etc."
            value={values.dados_coletados || ''}
            onChange={(e) => handleFieldChange('dados_coletados', e.target.value)}
          />
        </Box>

        {/* Restrições */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Restrições e proibições
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="O que os usuários não podem fazer? Ex: compartilhar conta, usar para fins ilegais, etc."
            value={values.restricoes || ''}
            onChange={(e) => handleFieldChange('restricoes', e.target.value)}
          />
        </Box>

        {/* Upload de Referência */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Termos de uso existentes ou documentos de referência (opcional)"
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="Os Termos de Uso serão elaborados em conformidade com a LGPD e legislação vigente." />
    </Box>
  );
};

// ============================================
// FORMULÁRIO: CONTRATO A PARTIR DE MODELO
// ============================================

export const ContratoModeloForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Criar Contrato a Partir de Modelo"
        customDescription="Adaptar um modelo de contrato existente para seu caso"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload do Modelo */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Anexe o modelo de contrato <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <DocumentUploadArea
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            onFileRemove={onFileRemove}
            suggestedDocs="Modelo de contrato a ser adaptado"
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
          />
        </Box>

        {/* Dados das Partes */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Dados das partes <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Informe os dados completos das partes: nome, CPF/CNPJ, endereço, etc."
            value={values.dados_partes || ''}
            onChange={(e) => handleFieldChange('dados_partes', e.target.value)}
          />
        </Box>

        {/* Adaptações Necessárias */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Adaptações necessárias <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Descreva as adaptações que precisam ser feitas no modelo: valores, prazos, condições específicas, cláusulas a adicionar ou remover..."
            value={values.adaptacoes || ''}
            onChange={(e) => handleFieldChange('adaptacoes', e.target.value)}
            inputProps={{ maxLength: 10000 }}
          />
        </Box>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio com detalhes adicionais sobre as adaptações necessárias."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <InfoTip message="A IA adaptará o modelo mantendo a estrutura original e aplicando as alterações solicitadas." />
    </Box>
  );
};

